import { find } from 'lodash';

export const ADD_TIMEZONE = 'event-widgets/timezones/ADD_TIMEZONE';

export function addTimezone(id, timezone) {
  return { type: ADD_TIMEZONE, payload: { id, timezone } };
}

/*
 * Represents a chunk of time when the dstOffset should be applied.
 */
export type DaylightSavingsTimeBlock = {
  utcStartTime: string,
  utcEndTime: string,
  dstOffset: number
};

/*
 * The offsets and display information for a single timezone.
 */
export type Timezone = {
  id: string,
  name: string,
  nameResourceKey: string,
  abbreviation: string,
  abbreviationResourceKey: string,
  utcOffset: number,
  dstInfo: ?Array<DaylightSavingsTimeBlock>
};

export type Timezones = { [string]: Timezone };

export default function reducer(state: Timezones = {}, action: any): Timezones {
  switch (action.type) {
    case ADD_TIMEZONE:
      return {
        ...state,
        [action.payload.id]: action.payload.timezone
      };
    default:
      return state;
  }
}

/*
 * Returns the dst block if present for the given date.
 */
const getDateDstInfo = (date, timezone, isUTCDate) => {
  return find(timezone.dstInfo || [], dstSection => {
    const startTime = new Date(dstSection.utcStartTime);
    const endTime = new Date(dstSection.utcEndTime);
    if (isUTCDate) {
      return startTime <= date && date < endTime;
    }
    // transforming all dates to local date before comparing with timezone's dates
    const dstOffsetInMs = dstSection.dstOffset * 60000;
    const timezoneOffsetInMs = timezone.utcOffset * 60000;
    const browserOffsetInMs = date.getTimezoneOffset() * 60000;
    const newDate = new Date(date.getTime() - browserOffsetInMs - dstOffsetInMs - timezoneOffsetInMs);
    return startTime <= newDate && newDate < endTime;
  });
};

/*
 * Takes a UTC date string and converts it to a date that will display correctly
 * for the provided timezone. The returned date's timezone information will not be
 * correct so the date should not be used for logic checking.
 * @param utcDateString - The string containing the UTC date in a format that
 *    which is parsible by the standard javascript date object.
 * @param timezone - The timezone the date must be displayed in.
 */
export const utcToDisplayDate = (utcDateString, timezone) => {
  const browserUtcDate = new Date(utcDateString);
  const dst = getDateDstInfo(browserUtcDate, timezone, true);
  // The browsers offset is used to counteract javascript automatically converting
  // the date to the browsers timezone.
  const browserTimezoneOffsetInMs = browserUtcDate.getTimezoneOffset() * 60000;
  const dstOffsetInMs = dst ? dst.dstOffset * 60000 : 0;
  const timezoneOffsetInMs = timezone.utcOffset * 60000;
  return new Date(browserUtcDate.getTime() + browserTimezoneOffsetInMs + dstOffsetInMs + timezoneOffsetInMs);
};

/*
 * Takes a display date and converts it to a ISO 8601 UTC date string using the provided
 * timezone as reference.
 * @param displayDate - The date to convert to a ISO 8601 UTC date.
 * @param timezone - The timezone used when calculating the UTC date.
 */
export const displayToUtcDateString = (displayDate, timezone) => {
  const dst = getDateDstInfo(displayDate, timezone);
  const browswerTimezoneOffsetInMs = displayDate.getTimezoneOffset() * 60000;
  const dstOffsetInMs = dst ? dst.dstOffset * 60000 : 0;
  const timezoneOffsetInMs = timezone.utcOffset * 60000;
  return new Date(
    displayDate.getTime() - browswerTimezoneOffsetInMs - dstOffsetInMs - timezoneOffsetInMs
  ).toISOString();
};
